import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { apiFetch, post, put, del } from "@/lib/api";
import { queryClient } from "@/lib/query-client";
import { keys } from "@/hooks/keys";
import { API_BASE } from "@/lib/utils";
import type { UnifiedEntityConfigForm } from "@/hooks/use-unified-config";

// ── Types ──────────────────────────────────────────────────────────────────────

export type SyncDirection = "inbound" | "outbound" | "bidirectional";

export type FormFieldType =
  | "text" | "textarea" | "number" | "email" | "url" | "password" | "color"
  | "select" | "multiselect" | "radio"
  | "checkbox"
  | "date" | "time" | "datetime-local"
  | "range"
  | "section";

export interface FormFieldOption {
  label: string;
  value: string;
}

export interface FormField {
  key: string;
  label: string;
  type: FormFieldType;
  placeholder?: string;
  description?: string;
  required?: boolean;
  defaultValue?: string | number | boolean | string[];
  options?: Array<string | FormFieldOption>;
  min?: number;
  max?: number;
  step?: number;
  rows?: number;
  /** Dynamic options via connector action. When set on a select/multiselect,
   *  options are fetched live instead of using the static options array. */
  enrich?: import("@/hooks/use-configs").EnrichSpec;
  /** Field keys this field depends on. The field is disabled until all
   *  dependencies have values, and clears when any dependency changes. */
  dependsOn?: string[];
}

export interface WidgetFormSchema {
  title?: string;
  description?: string;
  submitLabel?: string;
  fields: FormField[];
}

export type ActivationMode = "SINGLE_ACTIVATION" | "MULTI_CONNECTION";

export interface WidgetConnectorRef {
  connectorId: string;
  slug: string;
  // Optional widget-level overrides; unset means "use the connector's catalog
  // default". Scope (SaaS vs multi-tenant) and SaaS connection pin DO NOT
  // live here — they're workflow.connector_manifest concerns.
  authMethodId?: string;
  authProviderId?: string;
  deleteOnDeactivate?: boolean;
  // Presentation only: omit this connector's logo from the hub logo stack.
  // The connector itself stays in the widget — auth, connect and execution
  // are untouched.
  hideLogo?: boolean;
  // SaaS-connector connection scope (e.g. "inventory"). Only settable for
  // connectors registered under Settings > SaaS Connectors. At activation the
  // server pins the tenant's connection with this scope; the customer never
  // sees this connector in the connect flow. Not the manifest tenancy scope.
  connectionScope?: string | null;
}

/** The widget's per-connector overrides for one connector, as set in the
 *  builder's connector settings popup. Matches on connectorId first, then slug —
 *  catalog inheritance can surface the same logical app under a different
 *  connector_id at the partner vs the tenant scope, and the ref must still
 *  resolve so the developer's auth-provider pick is honoured at connect time. */
export function widgetConnectorRef(
  widget: { connectorRefs?: WidgetConnectorRef[] } | null | undefined,
  connector: { id: string; slug?: string } | null | undefined,
): WidgetConnectorRef | undefined {
  const refs = widget?.connectorRefs;
  if (!refs || refs.length === 0 || !connector) return undefined;
  return (
    refs.find((r) => r.connectorId === connector.id) ??
    (connector.slug ? refs.find((r) => r.slug === connector.slug) : undefined)
  );
}

export interface WidgetLifecycleHookRef {
  workflowId: string;
  version?: string;
  // Explicit on/off. Absent === enabled. Lets the developer mute a hook
  // without losing the workflow pin.
  enabled?: boolean;
  // "blocking" (default when absent) blocks the action until the workflow
  // accepts; "background" runs in the background and cannot veto.
  mode?: "blocking" | "background";
}

export interface WidgetLifecycleHooks {
  onActivation?: WidgetLifecycleHookRef | null;
  onDeactivation?: WidgetLifecycleHookRef | null;
  onConfigurationCreated?: WidgetLifecycleHookRef | null;
  onConfigurationChange?: WidgetLifecycleHookRef | null;
  onConfigurationDelete?: WidgetLifecycleHookRef | null;
  // Trigger lifecycle callbacks: per action the builder picks ONE of the
  // before/on pair (before = blocking can veto, create/update can also
  // rewrite settings; after = receives the trigger data). The installation
  // delete cascade only fires onTriggerDeleted.
  beforeTriggerCreated?: WidgetLifecycleHookRef | null;
  onTriggerCreated?: WidgetLifecycleHookRef | null;
  beforeTriggerUpdated?: WidgetLifecycleHookRef | null;
  onTriggerUpdated?: WidgetLifecycleHookRef | null;
  beforeTriggerDeleted?: WidgetLifecycleHookRef | null;
  onTriggerDeleted?: WidgetLifecycleHookRef | null;
}

export type WidgetTriggerType = "WEBHOOK" | "SCHEDULE" | "APP_EVENT";

export interface WidgetTriggerRatePreset {
  value: number;
  unit: "minutes" | "hours" | "days";
}

export interface WidgetTriggerCronPreset {
  label: string;
  expression: string;
}

export interface WidgetTriggerScheduleOptions {
  ratePresets?: WidgetTriggerRatePreset[];
  cronPresets?: WidgetTriggerCronPreset[];
  // Allow the end user to type a custom rate/cron instead of picking a preset.
  allowCustom?: boolean;
}

// Template-trigger configuration lives on the widget's trigger_refs — never on
// the trigger rows themselves. Mirrors TriggerRefSchema in services/api.
export interface WidgetTriggerRef {
  triggerId: string;
  type: WidgetTriggerType;
  endUserEditable: boolean;
  // SCHEDULE only: subset of ["cron", "rate", "timezone", "startsAt"].
  editableFields: string[];
  // e.g. { rate: { value, unit } } or { cron: { expression } }, plus timezone.
  defaultSettings: Record<string, unknown>;
  label?: string;
  scheduleOptions?: WidgetTriggerScheduleOptions;
  // Builder-defined headers merged into every created route binding. End-user
  // extras (when allowExtraHeaders) merge over these; x-installation-id and
  // x-end-org-id are always stamped last and can never be overridden.
  defaultHeaders?: Record<string, string>;
  allowExtraHeaders?: boolean;
  // widget.triggerCategories slugs this template belongs to. A template
  // provisions when ANY of its categories is selected at install time.
  // Absent/empty = core: always provisioned regardless of selection.
  categories?: string[];
}

// Named trigger groups (e.g. sync directions) the installer picks from at
// activation. Mirrors TriggerCategorySchema in services/api.
export interface WidgetTriggerCategory {
  slug: string;
  label: string;
  description?: string;
}

export interface Widget {
  id: string;
  orgId: string;
  name: string;
  slug: string;
  description: string | null;
  connectorIds: string[];
  workflowIds: string[];
  triggerIds: string[];
  agentContext: string | null;
  syncDirection: SyncDirection;
  styleOverrides: Record<string, string> | null;
  formSchema: WidgetFormSchema | null;
  status: string;
  // Typed refs / lifecycle / visibility (added by migration 093). Backwards-compat:
  // backend dual-writes both bare-ID arrays and typed refs, so old code reading
  // connectorIds keeps working while new code can read connectorRefs.
  connectorRefs?: WidgetConnectorRef[];
  // Per-trigger template settings (end-user editability, presets, defaults).
  // Authoritative over triggerIds when non-empty.
  triggerRefs?: WidgetTriggerRef[];
  // Trigger group catalog the install wizard offers for selection.
  triggerCategories?: WidgetTriggerCategory[];
  // Optional heading for the wizard's sync-type selection step; null falls
  // back to the built-in "What do you want to sync?".
  triggerCategoriesTitle?: string | null;
  // Unified catalog entities attached to this widget. Slugs/names are
  // resolved server-side from the platform catalog.
  unifiedRefs?: WidgetUnifiedRef[];
  // Derived server-side on every write: UNIFIED when unified entities are
  // attached, APP otherwise.
  type?: "APP" | "UNIFIED";
  activationMode?: ActivationMode;
  enabled?: boolean;
  allowedEndOrgIds?: string[] | null;
  lifecycleHooks?: WidgetLifecycleHooks;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface WidgetUnifiedRef {
  entityId: string;
  categorySlug: string;
  entitySlug: string;
  name: string;
  /** Providers chosen for this entity; resolved server-side, never empty. */
  connectorSlugs?: string[];
  /** Owner-defined per-provider configuration form. Keys are connector slugs
   *  within connectorSlugs. Omitted on PUT preserves the stored form. */
  configForm?: UnifiedEntityConfigForm;
}

/** True when the widget's unified entities define at least one config-form
 *  field. Unified widgets carry no config template until one is saved, so this
 *  — not the presence of a config row — is what decides whether a
 *  configuration surface has anything to show. */
export function widgetHasUnifiedForms(
  refs: WidgetUnifiedRef[] | null | undefined,
): boolean {
  return (refs ?? []).some(
    (r) => r.configForm && Object.values(r.configForm).some((fields) => fields.length > 0),
  );
}

export interface CreateWidgetInput {
  name: string;
  description?: string;
  connector_ids: string[];
  workflow_ids?: string[];
  trigger_ids?: string[];
  trigger_refs?: WidgetTriggerRef[];
  trigger_categories?: WidgetTriggerCategory[];
  trigger_categories_title?: string | null;
  unified_refs?: Array<{ entityId: string; connectorSlugs?: string[]; configForm?: UnifiedEntityConfigForm }>;
  agent_context?: string;
  sync_direction?: SyncDirection;
  style_overrides?: Record<string, string> | null;
  form_schema?: WidgetFormSchema | null;
  status?: "active" | "draft" | "archived";
}

export interface UpdateWidgetInput {
  name?: string;
  description?: string | null;
  connector_ids?: string[];
  connector_refs?: WidgetConnectorRef[];
  workflow_ids?: string[];
  trigger_ids?: string[];
  trigger_refs?: WidgetTriggerRef[];
  trigger_categories?: WidgetTriggerCategory[];
  trigger_categories_title?: string | null;
  unified_refs?: Array<{ entityId: string; connectorSlugs?: string[]; configForm?: UnifiedEntityConfigForm }>;
  agent_context?: string | null;
  sync_direction?: SyncDirection;
  style_overrides?: Record<string, string> | null;
  form_schema?: WidgetFormSchema | null;
  status?: "active" | "draft" | "archived";
  activation_mode?: ActivationMode;
  enabled?: boolean;
  allowed_end_org_ids?: string[] | null;
  lifecycle_hooks?: WidgetLifecycleHooks;
}

// ── Queries ────────────────────────────────────────────────────────────────────

export function useWidgets(endOrgId?: string, opts?: { enabled?: boolean }) {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  // When previewing a tenant ("view as <tenant>"), scope the catalog to that
  // tenant so the customer-view preview shows the tenant's OWN widgets plus
  // inherited account widgets — matching the embed. Cached under a distinct
  // key so it never clobbers the account-scoped management list.
  return useQuery({
    queryKey: keys.widgets.list(endOrgId ?? orgId),
    queryFn: () => apiFetch<Widget[]>(`${API_BASE}/api/v1/widgets${endOrgId ? `?endOrgId=${encodeURIComponent(endOrgId)}` : ""}`),
    enabled: !!orgId && (opts?.enabled ?? true),
  });
}

export function useWidget(id: string | undefined) {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  return useQuery({
    queryKey: keys.widgets.detail(orgId, id),
    queryFn: () => apiFetch<Widget>(`${API_BASE}/api/v1/widgets/${encodeURIComponent(id!)}`),
    enabled: !!orgId && !!id,
  });
}

// ── Mutations ──────────────────────────────────────────────────────────────────

export function useCreateWidget() {
  return useMutation({
    mutationFn: (input: CreateWidgetInput) =>
      apiFetch<Widget>(`${API_BASE}/api/v1/widgets`, post(input)),
    // Same reason as the update hook below: a create has to reach the
    // tenant-scoped preview list too.
    onSuccess: () =>
      queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === "widgets" }),
  });
}

export function useUpdateWidget() {
  return useMutation({
    mutationFn: ({ id, ...input }: UpdateWidgetInput & { id: string }) =>
      apiFetch<Widget>(`${API_BASE}/api/v1/widgets/${encodeURIComponent(id)}`, put(input)),
    onSuccess: () => {
      // Every widget query, not just this org's list. The "view as <tenant>" preview
      // caches under keys.widgets.list(tenantId) — a different key — so editing
      // an integration's customer visibility and publishing left the preview
      // rendering the state the change had just removed, and only F5 fixed it.
      // The admin was shown the opposite of what their own change did.
      queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === "widgets" });
    },
  });
}

export function useDeleteWidget() {
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ deleted: boolean; id: string }>(`${API_BASE}/api/v1/widgets/${encodeURIComponent(id)}`, del),
    onSuccess: () =>
      queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === "widgets" }),
  });
}
